import { Box, Text } from '@dixa/lib-shared-habla';
import { Message } from 'lib-shared-legacy-types';
import { useMemo } from 'react';

import styles from '../message/message.module.scss';

import TextMessage, { TextMessageProps } from './TextMessage';
import { getAttachments } from './utils/attachment';
import { useTranslatePlainTextConversationMessage } from './utils/useTranslatePlainTextConversationMessage';

type MessageTwitterProps = {
  message: Message;
} & TextMessageProps;

export const MessageTwitter = ({
  first,
  conversationAttributes,
  ...otherProps
}: MessageTwitterProps): JSX.Element => {
  const { button, translation } = useTranslatePlainTextConversationMessage({
    text: otherProps.message.text ?? undefined,
    originalLanguageCode: otherProps.message.language ?? null,
  });

  const attachments = useMemo(
    () => getAttachments(otherProps.message, { scaleImage: true }),
    [otherProps.message],
  );

  const isPublicTweet = conversationAttributes?.is_public_content;
  const endUserHandle =
    otherProps.message.from_contact_point?.attributes?.screen_name;

  const twitterMessageDescription = useMemo(() => {
    const integrationContactPointHandle =
      conversationAttributes?.integration_contact_point_handle;

    const showDetails = integrationContactPointHandle && first;
    if (isPublicTweet === 'true' && endUserHandle) {
      return (
        <Box margin="0 0 0 200">
          <Text.Paragraph variant="4" subdued>
            {showDetails ? (
              <>
                @{endUserHandle}{' '}
                {getMentionedOriginalLinkOrPlainText(
                  endUserHandle,
                  otherProps.message.external_id,
                )}{' '}
                @{integrationContactPointHandle}
              </>
            ) : (
              `@${endUserHandle}`
            )}
          </Text.Paragraph>
        </Box>
      );
    }
  }, [
    conversationAttributes?.integration_contact_point_handle,
    endUserHandle,
    first,
    isPublicTweet,
    otherProps.message.external_id,
  ]);

  return (
    <TextMessage
      {...otherProps}
      showMarkupAndContent
      attachments={attachments}
      authorDescription={twitterMessageDescription}
      gutterButtons={button}
      translation={translation}
    />
  );
};

function getMentionedOriginalLinkOrPlainText(
  endUserHandle: string,
  twitterMessageId?: string | null,
): JSX.Element {
  const mentionedText = 'mentioned';
  if (twitterMessageId) {
    return (
      <a
        role="button"
        className={styles.mentionedLink}
        onClick={() => {
          window.open(
            `https://twitter.com/${endUserHandle}/status/${twitterMessageId}`,
            '_blank',
          );
        }}
      >
        {mentionedText}
      </a>
    );
  }
  return <>{mentionedText}</>;
}
